import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography, Form, DatePicker, Select, Button, Space, Table, Progress,
  Card, Empty, Spin, Segmented, Tag,
} from 'antd';
import ResizableTitle from '../components/ResizableTitle';
import { CalendarOutlined, UnorderedListOutlined, DownloadOutlined, TagOutlined, LineChartOutlined, AppstoreOutlined, WarningOutlined } from '@ant-design/icons';
import {
  ComposedChart, BarChart, Bar, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';

import { selectFilter } from '../utils/selectFilter';
import { scrollWidth } from '../utils/tableLayout';
const CHART_COLORS = ['#1677ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#13c2c2', '#fa541c', '#2f54eb'];

const { RangePicker } = DatePicker;

function scoreColor(pct) {
  if (pct == null) return '#8c8c8c';
  if (pct >= 80) return '#52c41a';
  if (pct >= 60) return '#faad14';
  return '#ff4d4f';
}

function ScoreBarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ color: scoreColor(d.AvgScore) }}>Avg Score: <strong>{d.AvgScore}%</strong></div>
      <div style={{ color: '#595959' }}>Completed evals: {d.EvalCount}</div>
      {d.AvgTargetScore != null && <div style={{ color: '#1677ff', marginTop: 4 }}>Target (Gold): {d.AvgTargetScore}%</div>}
      {d.AvgHighScore   != null && <div style={{ color: '#722ed1' }}>Platinum: {d.AvgHighScore}%</div>}
    </div>
  );
}

function ScoreBarLabel({ x, y, width, value }) {
  if (value == null) return null;
  return (
    <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={12} fontWeight={600} fill="#595959">
      {value}%
    </text>
  );
}

function ScoreBar({ pct }) {
  if (pct == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Progress percent={pct} showInfo={false} strokeColor={scoreColor(pct)}
        size="small" style={{ flex: 1, minWidth: 80 }} />
      <Typography.Text strong style={{ color: scoreColor(pct), whiteSpace: 'nowrap' }}>
        {pct}%
      </Typography.Text>
    </div>
  );
}

// Shared column definitions
const SCORE_COL  = { title: 'Avg Score',         dataIndex: 'AvgPct',      key: 'AvgPct',      width: 190, sorter: (a, b) => (a.AvgPct ?? -1) - (b.AvgPct ?? -1), render: (p) => <ScoreBar pct={p} /> };
const EARNED_COL = { title: 'Total Pts',          dataIndex: 'TotalEarned', key: 'TotalEarned', width: 100, align: 'right', sorter: (a, b) => a.TotalEarned - b.TotalEarned };
const MAX_COL    = { title: 'Total Possible Pts', dataIndex: 'TotalMax',    key: 'TotalMax',    width: 150, align: 'right', sorter: (a, b) => a.TotalMax - b.TotalMax };
const MONTH_COL    = { title: 'Call Date Month', dataIndex: 'Month',    key: 'Month',    width: 150, sorter: (a, b) => (a.MonthKey ?? '').localeCompare(b.MonthKey ?? '') };
const CAMPAIGN_COL = { title: 'Campaign',        dataIndex: 'Campaign', key: 'Campaign', width: 160, sorter: (a, b) => (a.Campaign ?? '').localeCompare(b.Campaign ?? '') };

const OCC_COL = {
  title: 'Occurrences', dataIndex: 'Occurrences', key: 'Occurrences', width: 115, align: 'right',
  sorter: (a, b) => (a.Occurrences ?? 0) - (b.Occurrences ?? 0),
  render: (v) => v > 0 ? <Tag color="orange" style={{ margin: 0 }}>{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
};

const sectionColumns = (mode) => [
  ...(mode === 'month'    ? [MONTH_COL]    : []),
  ...(mode === 'campaign' ? [CAMPAIGN_COL] : []),
  { title: 'Section', dataIndex: 'SectionName', key: 'SectionName', sorter: (a, b) => a.SectionName.localeCompare(b.SectionName) },
  { title: '# Cases',  dataIndex: 'CaseCount',   key: 'CaseCount',  width: 85, align: 'right', sorter: (a, b) => a.CaseCount - b.CaseCount },
  OCC_COL, SCORE_COL, EARNED_COL, MAX_COL,
];

const questionColumns = (mode) => [
  ...(mode === 'month'    ? [MONTH_COL]    : []),
  ...(mode === 'campaign' ? [CAMPAIGN_COL] : []),
  { title: 'Section',  dataIndex: 'SectionName',  key: 'SectionName',  width: 160, ellipsis: true, sorter: (a, b) => a.SectionName.localeCompare(b.SectionName) },
  { title: 'Question', dataIndex: 'QuestionText',  key: 'QuestionText', width: 320, ellipsis: true, sorter: (a, b) => a.QuestionText.localeCompare(b.QuestionText) },
  { title: '# Answers', dataIndex: 'AnswerCount', key: 'AnswerCount', width: 95, align: 'right', sorter: (a, b) => a.AnswerCount - b.AnswerCount },
  SCORE_COL, EARNED_COL, MAX_COL,
];

function exportToExcel(rows, mode, type) {
  const data = rows.map((r) => {
    const row = {};
    if (mode === 'month')    row['Month']    = r.Month;
    if (mode === 'campaign') row['Campaign'] = r.Campaign;
    row['Section'] = r.SectionName;
    if (type === 'questions') {
      row['Question']  = r.QuestionText;
      row['# Answers'] = r.AnswerCount;
    } else {
      row['# Cases']      = r.CaseCount;
      row['Occurrences']  = r.Occurrences ?? 0;
    }
    row['Avg Score (%)']     = r.AvgPct ?? '';
    row['Total Pts']         = r.TotalEarned;
    row['Total Possible Pts'] = r.TotalMax;
    return row;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, type === 'questions' ? 'Questions' : 'Sections');
  XLSX.writeFile(wb, `eval-analytics-${type}.xlsx`);
}

const MODE_OPTIONS = [
  { label: 'Summary',          value: 'summary',      icon: <UnorderedListOutlined /> },
  { label: 'By Month',         value: 'month',        icon: <CalendarOutlined /> },
  { label: 'By Month Pivot',   value: 'month_pivot',  icon: <AppstoreOutlined /> },
  { label: 'Occ. by Month',    value: 'occ_pivot',    icon: <WarningOutlined /> },
  { label: 'By Month Chart',   value: 'month_chart',  icon: <LineChartOutlined /> },
  { label: 'By Campaign',      value: 'campaign',     icon: <TagOutlined /> },
];
const Q_MODE_OPTIONS = MODE_OPTIONS.filter((o) => o.value !== 'occ_pivot');

/**
 * `embedded` renders this page's content without its own heading or filter
 * controls, scoped by the campaign and window passed in. The Campaign Summary
 * uses it so there is one implementation of these numbers rather than two that
 * can drift apart.
 */
export default function EvalAnalyticsPage({ embedded = false, campaignId = null, from = null, to = null, skills = null }) {
  const [form] = Form.useForm();
  const [activeParams, setActiveParams]   = useState(null);
  const [colWidths, setColWidths] = useState({});

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  const [scoresByMonth, setScoresByMonth]       = useState([]);
  const [scoresLoading, setScoresLoading]       = useState(false);
  const [scoresChartMode, setScoresChartMode]   = useState('summary');

  const [sectionMode, setSectionMode]     = useState('summary');
  const [sections, setSections]           = useState([]);
  const [sectionLoading, setSectionLoading] = useState(false);

  const [questionMode, setQuestionMode]   = useState('summary');
  const [questions, setQuestions]         = useState([]);
  const [questionLoading, setQuestionLoading] = useState(false);

  const [agentOptions, setAgentOptions]       = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [skillOptions, setSkillOptions]       = useState([]);
  const [formOptions, setFormOptions]         = useState([]);

  // Default call date to current month on first load
  useEffect(() => {
    if (embedded) return;
    form.setFieldsValue({ dateRange: [dayjs().startOf('month'), dayjs()] });
  }, []); // eslint-disable-line

  // Embedded, the campaign and window come from the page above. activeParams
  // is what every panel below reads, so setting it is the whole of it -- no
  // form to fill in and no Run button to press.
  useEffect(() => {
    if (!embedded) return;
    setActiveParams({
      ...(campaignId ? { campaign: String(campaignId) } : {}),
      ...(from ? { start_date: from } : {}),
      ...(to ? { end_date: to } : {}),
      // The Campaign Summary's Skill Group, as its skills.
      ...(skills?.length ? { skill: skills.join(',') } : {}),
    });
  }, [embedded, campaignId, from, to, skills]);

  useEffect(() => {
    api.get('/api/agent-stats/options')
      .then((r) => {
        setAgentOptions(r.data.agents.map((a) => ({ label: a.name, value: a.id ?? a.name })));
        setCampaignOptions(r.data.campaigns.map((c) => ({ label: c.Name, value: c.ID })));
        setSkillOptions((r.data.skills || []).map((s) => ({ label: s, value: s })));
      })
      .catch(() => {});
    api.get('/api/eval-forms/names')
      .then((r) => setFormOptions(r.data.map((n) => ({ label: n, value: n }))))
      .catch(() => {});
  }, []);

  // month_chart and month_pivot both use the same API group_by=month
  const secApiMode = ['month_chart', 'month_pivot', 'occ_pivot'].includes(sectionMode)  ? 'month' : sectionMode;
  const qApiMode   = ['month_chart', 'month_pivot'].includes(questionMode) ? 'month' : questionMode;

  useEffect(() => {
    if (!activeParams) { setScoresByMonth([]); return; }
    setScoresLoading(true);
    api.get('/api/eval-analytics/scores-by-month', { params: { ...activeParams, group_by: scoresChartMode } })
      .then((r) => setScoresByMonth(r.data))
      .catch(() => setScoresByMonth([]))
      .finally(() => setScoresLoading(false));
  }, [activeParams, scoresChartMode]);

  useEffect(() => {
    if (!activeParams) return;
    setSectionLoading(true);
    api.get('/api/eval-analytics/sections', { params: { ...activeParams, group_by: secApiMode } })
      .then((r) => setSections(r.data))
      .catch(() => setSections([]))
      .finally(() => setSectionLoading(false));
  }, [activeParams, secApiMode]);

  useEffect(() => {
    if (!activeParams) return;
    setQuestionLoading(true);
    api.get('/api/eval-analytics/questions', { params: { ...activeParams, group_by: qApiMode } })
      .then((r) => setQuestions(r.data))
      .catch(() => setQuestions([]))
      .finally(() => setQuestionLoading(false));
  }, [activeParams, qApiMode]);

  // Pivot month rows into chart-friendly shape: [{ month, Section1: pct, Section2: pct, ... }]
  const pivotMonthChart = (rows) => {
    const monthOrder = [];
    const monthSeen  = new Set();
    rows.forEach((r) => { if (!monthSeen.has(r.MonthKey)) { monthSeen.add(r.MonthKey); monthOrder.push({ key: r.MonthKey, label: r.Month }); } });
    monthOrder.sort((a, b) => a.key.localeCompare(b.key));
    const sectionNames = [...new Set(rows.map((r) => r.SectionName))].sort();
    const lookup = {};
    rows.forEach((r) => { lookup[`${r.MonthKey}|${r.SectionName}`] = r.AvgPct; });
    const data = monthOrder.map(({ key, label }) => {
      const pt = { monthKey: key, month: label };
      sectionNames.forEach((s) => { pt[s] = lookup[`${key}|${s}`] ?? null; });
      return pt;
    });
    return { data, sectionNames };
  };

  // For question chart: aggregate questions → section avg per month, then pivot
  const pivotQuestionChart = (rows) => {
    const agg = {};
    rows.forEach((r) => {
      const k = `${r.MonthKey}|${r.SectionName}`;
      if (!agg[k]) agg[k] = { MonthKey: r.MonthKey, Month: r.Month, SectionName: r.SectionName, sum: 0, cnt: 0 };
      if (r.AvgPct != null) { agg[k].sum += r.AvgPct; agg[k].cnt++; }
    });
    const aggregated = Object.values(agg).map((g) => ({
      MonthKey: g.MonthKey, Month: g.Month, SectionName: g.SectionName,
      AvgPct: g.cnt > 0 ? Math.round(g.sum / g.cnt) : null,
    }));
    return pivotMonthChart(aggregated);
  };

  const sectionChart  = useMemo(() => sectionMode  === 'month_chart' ? pivotMonthChart(sections)     : null, [sections,  sectionMode]);
  const questionChart = useMemo(() => questionMode === 'month_chart' ? pivotQuestionChart(questions) : null, [questions, questionMode]);

  // Build pivot table data for By Month Pivot mode
  const buildPivotTable = (rows, keyFn, labelFn, valueKey = 'AvgPct') => {
    const months = [...new Set(rows.map((r) => r.MonthKey))].sort();
    const monthLabels = {};
    rows.forEach((r) => { monthLabels[r.MonthKey] = r.Month; });
    const seen = [];
    const map = {};
    rows.forEach((r) => {
      const k = keyFn(r);
      if (!map[k]) { map[k] = { _key: k, ...labelFn(r) }; seen.push(k); }
      map[k][r.MonthKey] = r[valueKey];
    });
    return { pivotData: seen.map((k) => map[k]), months, monthLabels };
  };

  const sectionPivot  = useMemo(() => sectionMode  === 'month_pivot'
    ? buildPivotTable(sections,  (r) => r.SectionName, (r) => ({ SectionName: r.SectionName }))
    : null, [sections,  sectionMode]); // eslint-disable-line

  const sectionOccPivot = useMemo(() => sectionMode === 'occ_pivot'
    ? buildPivotTable(sections, (r) => r.SectionName, (r) => ({ SectionName: r.SectionName }), 'Occurrences')
    : null, [sections, sectionMode]); // eslint-disable-line

  const questionPivot = useMemo(() => questionMode === 'month_pivot'
    ? buildPivotTable(questions, (r) => `${r.SectionName}|${r.QuestionText}`, (r) => ({ SectionName: r.SectionName, QuestionText: r.QuestionText }))
    : null, [questions, questionMode]); // eslint-disable-line

  const PivotTable = ({ pivot, type, renderCell }) => {
    if (!pivot || pivot.pivotData.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />;
    }
    const { pivotData, months, monthLabels } = pivot;
    const fixedCols = [
      { title: 'Section', dataIndex: 'SectionName', key: '__section', width: 180, ellipsis: true,
        fixed: 'left', onCell: (r, idx) => {
          if (type !== 'questions') return {};
          // Span repeated section names
          const prev = idx > 0 ? pivotData[idx - 1].SectionName : null;
          if (r.SectionName === prev) return { rowSpan: 0 };
          const span = pivotData.filter((x) => x.SectionName === r.SectionName).length;
          return { rowSpan: span };
        },
      },
      ...(type === 'questions' ? [{
        title: 'Question', dataIndex: 'QuestionText', key: '__question', width: 260, ellipsis: true, fixed: 'left',
      }] : []),
    ];
    const monthCols = months.map((mk) => ({
      title: monthLabels[mk] || mk,
      key: mk,
      width: 110,
      align: 'center',
      render: (_, r) => {
        const val = r[mk];
        if (val == null) return <span style={{ color: '#d9d9d9' }}>—</span>;
        if (renderCell) return renderCell(val);
        return <span style={{ color: scoreColor(val), fontWeight: 600 }}>{val}%</span>;
      },
    }));
    return (
      <Table
        rowKey="_key"
        dataSource={pivotData}
        columns={[...fixedCols, ...monthCols]}
        size="small"
        pagination={false}
        scroll={{ x: scrollWidth([...fixedCols, ...monthCols]) }}
        bordered
        locale={{ emptyText: 'No data for current filters.' }}
        components={{ header: { cell: ResizableTitle } }}
      />
    );
  };

  const buildParams = (values) => {
    const p = {};
    if (values.agent_name != null) {
      if (typeof values.agent_name === 'number') p.agent_user_id = values.agent_name;
      else p.agent_name = values.agent_name;
    }
    if (values.dateRange?.[0])        p.start_date         = values.dateRange[0].format('YYYY-MM-DD');
    if (values.dateRange?.[1])        p.end_date           = values.dateRange[1].format('YYYY-MM-DD');
    if (values.evalCreatedRange?.[0]) p.eval_created_start = values.evalCreatedRange[0].format('YYYY-MM-DD');
    if (values.evalCreatedRange?.[1]) p.eval_created_end   = values.evalCreatedRange[1].format('YYYY-MM-DD');
    if (values.campaign?.length)  p.campaign  = values.campaign.join(',');
    if (values.form_name?.length) p.form_name = values.form_name.join(',');
    if (values.skill?.length)     p.skill     = values.skill.join(',');
    if (values.excludeTermedFrom) p.exclude_termed_from = values.excludeTermedFrom.format('YYYY-MM-DD');
    return p;
  };


  const secRowKey = sectionMode === 'month'
    ? (r) => `${r.MonthKey}|${r.SectionName}`
    : sectionMode === 'campaign'
    ? (r) => `${r.Campaign}|${r.SectionName}`
    : 'SectionName';

  const qRowKey = questionMode === 'month'
    ? (r) => `${r.MonthKey}|${r.SectionName}|${r.QuestionText}`
    : questionMode === 'campaign'
    ? (r) => `${r.Campaign}|${r.SectionName}|${r.QuestionText}`
    : (r) => `${r.SectionName}|${r.QuestionText}`;

  const MonthChart = ({ chartData }) => {
    if (!chartData || chartData.data.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />;
    }
    const { data, sectionNames } = chartData;
    return (
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} margin={{ top: 16, right: 24, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} width={45} />
          <RechartTooltip formatter={(v) => v != null ? `${v}%` : '—'} />
          <Legend />
          {sectionNames.map((name, i) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              stroke={CHART_COLORS[i % CHART_COLORS.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const cardExtra = (rows, mode, type, loading, onModeChange) => (
    <Space>
      <Segmented size="small" value={mode} onChange={onModeChange} options={type === 'questions' ? Q_MODE_OPTIONS : MODE_OPTIONS} />
      <Button
        size="small"
        icon={<DownloadOutlined />}
        disabled={rows.length === 0 || loading}
        onClick={() => exportToExcel(rows, mode, type)}
      >
        Export
      </Button>
    </Space>
  );

  return (
    <>
      {!embedded && (
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        Eval Analytics
      </Typography.Title>
      )}

      {!embedded && (
      <Form
        form={form}
        layout="inline"
        onFinish={(v) => setActiveParams(buildParams(v))}
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item name="agent_name" label="Agent">
          <Select allowClear showSearch filterOption={selectFilter} placeholder="All agents" options={agentOptions} style={{ width: 240 }} />
        </Form.Item>

        <Form.Item name="dateRange" label="Call Date">
          <RangePicker style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="evalCreatedRange" label="Eval Created">
          <RangePicker style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="campaign" label="Campaign">
          <Select mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="All campaigns"
            options={campaignOptions} maxTagCount="responsive" style={{ width: 260 }} />
        </Form.Item>

        <Form.Item name="form_name" label="Eval Form">
          <Select mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="All forms"
            options={formOptions} maxTagCount="responsive" style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="skill" label="Skill">
          <Select mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="All skills"
            options={skillOptions} maxTagCount="responsive" style={{ width: 240 }} />
        </Form.Item>

        <Form.Item name="excludeTermedFrom" label="Exclude w/ Term Date On/After">
          <DatePicker style={{ width: 150 }} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">Run Analytics</Button>
            <Button onClick={() => {
              form.resetFields();
              setActiveParams(null);
              setScoresByMonth([]);
              setScoresChartMode('summary');
              setSections([]);
              setQuestions([]);
              setSectionMode('summary');
              setQuestionMode('summary');
            }}>
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
      )}

      {activeParams === null && (
        <Empty description="Set your filters above and click Run Analytics" style={{ marginTop: 60 }} />
      )}

      {activeParams !== null && (
        <>
          <Card
            size="small"
            title="Avg Eval Score by Month (Call Date)"
            style={{ marginBottom: 16 }}
            extra={
              <Segmented
                size="small"
                value={scoresChartMode}
                onChange={setScoresChartMode}
                options={[
                  { label: 'Summary',     value: 'summary',  icon: <UnorderedListOutlined /> },
                  { label: 'By Campaign', value: 'campaign', icon: <TagOutlined /> },
                ]}
              />
            }
          >
            {scoresLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>
            ) : scoresByMonth.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No completed evaluations match your filters" />
            ) : scoresChartMode === 'campaign' ? (() => {
              const campaigns = [...new Set(scoresByMonth.flatMap((r) => Object.keys(r).filter((k) => k !== 'MonthKey' && k !== 'Month')))].sort();
              return (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={scoresByMonth} margin={{ top: 16, right: 20, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="Month" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} width={42} />
                    <RechartTooltip formatter={(v) => v != null ? `${v}%` : '—'} />
                    <Legend />
                    {campaigns.map((camp, i) => (
                      <Bar key={camp} dataKey={camp} name={camp} fill={CHART_COLORS[i % CHART_COLORS.length]}
                        radius={[3, 3, 0, 0]} maxBarSize={40} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              );
            })() : (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={scoresByMonth} margin={{ top: 24, right: 20, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="Month" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} width={42} />
                  <RechartTooltip content={<ScoreBarTooltip />} />
                  {(scoresByMonth.some((d) => d.AvgTargetScore != null) ||
                    scoresByMonth.some((d) => d.AvgHighScore   != null)) && (
                    <Legend verticalAlign="top" align="right" iconType="plainline"
                      payload={[
                        ...(scoresByMonth.some((d) => d.AvgTargetScore != null) ? [{ value: 'Target (Gold)', type: 'plainline', color: '#1677ff' }] : []),
                        ...(scoresByMonth.some((d) => d.AvgHighScore   != null) ? [{ value: 'Platinum',      type: 'plainline', color: '#722ed1' }] : []),
                      ]}
                    />
                  )}
                  <Bar dataKey="AvgScore" name="Avg Score" radius={[4, 4, 0, 0]} maxBarSize={72}
                    label={<ScoreBarLabel />}>
                    {scoresByMonth.map((entry) => (
                      <Cell key={entry.MonthKey} fill={scoreColor(entry.AvgScore)} />
                    ))}
                  </Bar>
                  {scoresByMonth.some((d) => d.AvgTargetScore != null) && (
                    <Line type="monotone" dataKey="AvgTargetScore" name="Target (Gold)"
                      stroke="#1677ff" strokeWidth={2} strokeDasharray="5 3"
                      dot={{ r: 4, fill: '#1677ff', strokeWidth: 0 }} activeDot={{ r: 5 }}
                      connectNulls legendType="none" />
                  )}
                  {scoresByMonth.some((d) => d.AvgHighScore != null) && (
                    <Line type="monotone" dataKey="AvgHighScore" name="Platinum"
                      stroke="#722ed1" strokeWidth={2} strokeDasharray="5 3"
                      dot={{ r: 4, fill: '#722ed1', strokeWidth: 0 }} activeDot={{ r: 5 }}
                      connectNulls legendType="none" />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card
            title="Section Stats"
            size="small"
            style={{ marginBottom: 16 }}
            extra={cardExtra(sections, sectionMode, 'sections', sectionLoading, setSectionMode)}
          >
            {sectionLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>
            ) : sections.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No completed evaluations match your filters" />
            ) : sectionMode === 'month_chart' ? (
              <MonthChart chartData={sectionChart} />
            ) : sectionMode === 'month_pivot' ? (
              <PivotTable pivot={sectionPivot} type="sections" />
            ) : sectionMode === 'occ_pivot' ? (
              <PivotTable pivot={sectionOccPivot} type="sections"
                renderCell={(v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>}
              />
            ) : (
              <Table
                rowKey={secRowKey}
                dataSource={sections}
                columns={sectionColumns(sectionMode).map(toResizable)}
                size="small"
                pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                components={{ header: { cell: ResizableTitle } }}
              />
            )}
          </Card>

          <Card
            title="Question Stats"
            size="small"
            extra={cardExtra(questions, questionMode, 'questions', questionLoading, setQuestionMode)}
          >
            {questionLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>
            ) : questions.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No completed evaluations match your filters" />
            ) : questionMode === 'month_chart' ? (
              <MonthChart chartData={questionChart} />
            ) : questionMode === 'month_pivot' ? (
              <PivotTable pivot={questionPivot} type="questions" />
            ) : (
              <Table
                rowKey={qRowKey}
                dataSource={questions}
                columns={questionColumns(questionMode).map(toResizable)}
                size="small"
                tableLayout="fixed"
                pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                components={{ header: { cell: ResizableTitle } }}
              />
            )}
          </Card>
        </>
      )}
    </>
  );
}
